/**
 * Формирование ключей и параметров строк журнала операций (§Э1.4, §19.5).
 *
 * Назначение файла: превращает код операции сервера в ключ каталога economy:ledger.op.*
 * без текстовых литералов в компонентах.
 */

export type LedgerEntry = {
  readonly id: number;
  readonly op: string;
  readonly amount: number;
  readonly currency: "hive" | "cash";
  readonly hexId: string | null;
  readonly createdAt: string;
  readonly params: Record<string, unknown>;
};

export type LedgerLineView = {
  readonly i18nKey: string;
  readonly signedAmount: number;
  readonly currency: "hive" | "cash";
  readonly createdAt: string;
  readonly params: Record<string, string | number>;
};

const KNOWN_OPS = new Set([
  "income_hex",
  "income_hearth",
  "income_lodgers",
  "income_trace",
  "income_work",
  "income_quest",
  "income_defence",
  "income_recovery",
  "tribute_in",
  "tribute_out",
  "tribute_settle_in",
  "tribute_unpaid",
  "siege_hold",
  "siege_return",
  "siege_lost",
  "reparation_in",
  "trophy_in",
  "debt_penalty",
  "debt_repay",
  "debt_auction",
  "shop_hive",
  "shop_cash",
  "shop_wick",
  "convert",
  "burn",
  "clan_in",
  "clan_out",
  "ritual_stake",
  "ritual_award",
]);

/** Ключ каталога для кода операции журнала. */
export function ledgerOpI18nKey(op: string): string {
  return KNOWN_OPS.has(op) ? `ledger.op.${op}` : "ledger.op.unknown";
}

/** Знак суммы по коду операции: списания отрицательные. */
export function ledgerSignedAmount(entry: LedgerEntry): number {
  const outOps = new Set([
    "tribute_out",
    "debt_penalty",
    "debt_auction",
    "shop_hive",
    "shop_cash",
    "shop_wick",
    "siege_lost",
    "burn",
    "clan_out",
    "ritual_stake",
  ]);
  if (outOps.has(entry.op)) return -entry.amount;
  if (entry.op === "convert") return entry.amount;
  return entry.amount;
}

/** Строка журнала для отображения в списке. */
export function ledgerLineView(entry: LedgerEntry): LedgerLineView {
  const params: Record<string, string | number> = { amount: entry.amount };
  if (entry.hexId) {
    params["hexId"] = entry.hexId.slice(-6);
  }
  for (const [key, value] of Object.entries(entry.params)) {
    if (typeof value === "string" || typeof value === "number") {
      params[key] = value;
    }
  }

  return {
    i18nKey: ledgerOpI18nKey(entry.op),
    signedAmount: ledgerSignedAmount(entry),
    currency: entry.currency,
    createdAt: entry.createdAt,
    params,
  };
}
